import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { User } from 'firebase/auth'
import type { AuthUser, UserRole } from '../types/user'

interface AuthState {
  // State
  firebaseUser: User | null
  authUser: AuthUser | null
  isLoading: boolean
  isInitialized: boolean
  error: string | null

  // Actions
  setFirebaseUser: (user: User | null) => void
  setAuthUser: (user: AuthUser | null) => void
  setLoading: (loading: boolean) => void
  setInitialized: (initialized: boolean) => void
  setError: (error: string | null) => void
  reset: () => void

  // Computed helpers
  isLoggedIn: () => boolean
  isTeacher: () => boolean
  isSuperAdmin: () => boolean
  isStudent: () => boolean
  hasRole: (role: UserRole) => boolean
}

const initialState = {
  firebaseUser: null,
  authUser: null,
  isLoading: true,
  isInitialized: false,
  error: null,
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    setFirebaseUser: (user) => set({ firebaseUser: user }),
    setAuthUser: (user) => set({ authUser: user }),
    setLoading: (loading) => set({ isLoading: loading }),
    setInitialized: (initialized) => set({ isInitialized: initialized }),
    setError: (error) => set({ error }),
    reset: () => set(initialState),

    isLoggedIn: () => get().authUser !== null,
    isTeacher: () => get().authUser?.role === 'teacher',
    isSuperAdmin: () => get().authUser?.role === 'superadmin',
    isStudent: () => get().authUser?.role === 'student',
    hasRole: (role) => get().authUser?.role === role,
  }))
)
